import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  useActiveFixturesSummary,
  useBatchPublishSuspensions,
  useCreateBatchPublishSuspension,
  useFilterLeagues,
  useFilterMarketIds,
  useQuotesByFixture,
  useRemoveBatchPublishSuspension,
} from '../api/hooks';
import { useAuth } from '../auth/AuthContext';
import { useHomebaseModal } from '../components/HomebaseModal';
import {
  BATCH_SUSPENSION_SCOPE_OPTIONS,
  BATCH_SUSPENSION_SPORT_BUCKETS,
  batchSuspensionFixtureLabel,
  batchSuspensionScopeLabel,
  formatBatchSuspensionTarget,
  isFixtureBatchScope,
  normalizeBatchSuspensionFixture,
  sportBucketForFixture,
} from '../utils/batchSuspensionUtils';
import './BatchSuspensionsPage.css';

// Sentinel select value: keeps the field in club mode while the text input supplies the name.
const OTHER_CLUB = '__other__';

function formatApiError(err) {
  const d = err?.response?.data;
  if (typeof d?.detail === 'string') return d.detail;
  if (Array.isArray(d?.detail)) {
    return d.detail.map((x) => x.msg || JSON.stringify(x)).join('; ');
  }
  return err?.message || 'Request failed';
}

function formatTs(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return String(iso);
  }
}

export default function BatchSuspensionsPage() {
  const { user } = useAuth();
  const { confirm, ModalLayer } = useHomebaseModal();
  const [sportBucket, setSportBucket] = useState('soccer');
  const [listSportFilter, setListSportFilter] = useState('all');
  const [scope, setScope] = useState('fixture');
  const [fixtureId, setFixtureId] = useState('');
  const [playerId, setPlayerId] = useState('');
  const [teamSide, setTeamSide] = useState('');
  const [teamOtherMode, setTeamOtherMode] = useState(false);
  const [league, setLeague] = useState('');
  const [wagerType, setWagerType] = useState('');
  const [reason, setReason] = useState('Fixture suspension');
  const [createdBy, setCreatedBy] = useState('');
  const [confirmAll, setConfirmAll] = useState(false);
  const [fanoutWriters, setFanoutWriters] = useState(true);
  const [lastError, setLastError] = useState(null);
  const [lastSuccess, setLastSuccess] = useState(null);

  const listBucketParam = listSportFilter === 'all' ? null : listSportFilter;
  const { data, isLoading, isError, error, refetch } = useBatchPublishSuspensions(
    true,
    listBucketParam
  );
  const suspensions = useMemo(
    () => (Array.isArray(data?.suspensions) ? data.suspensions : []),
    [data]
  );

  const { data: fixturesPayload, isLoading: fixturesLoading } = useActiveFixturesSummary();
  const fixtures = useMemo(
    () =>
      (fixturesPayload?.fixtures || [])
        .map(normalizeBatchSuspensionFixture)
        .filter((f) => f?.game_id)
        .sort((a, b) => {
          const ta = a.start_time_utc ? new Date(a.start_time_utc).getTime() : 0;
          const tb = b.start_time_utc ? new Date(b.start_time_utc).getTime() : 0;
          return ta - tb;
        }),
    [fixturesPayload]
  );

  const selectedFixture = useMemo(
    () => fixtures.find((f) => f.game_id === fixtureId) || null,
    [fixtures, fixtureId]
  );

  const { data: fixtureQuotes = [], isLoading: quotesLoading } = useQuotesByFixture(fixtureId, {
    enabled: (scope === 'fixture_player' || scope === 'fixture_team') && Boolean(fixtureId),
  });

  const playerOptions = useMemo(() => {
    const byId = new Map();
    for (const q of fixtureQuotes) {
      const id = q.player_id;
      if (!id) continue;
      byId.set(id, q.player_name || id.replace(/_/g, ' '));
    }
    return [...byId.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [fixtureQuotes]);

  // A futures season shell has no home/away to take a side of, so ``team_side`` carries a club
  // selector instead (backend: batch_publish_suspension.team_side_is_club → label_matches_team
  // against the quote's player_team / player_team_abbr). Clubs are discovered from the shell's own
  // quotes so the operator picks from what is actually priced.
  const fixtureHasSides = Boolean(selectedFixture?.home_team || selectedFixture?.away_team);
  const teamSelectValue = useMemo(() => {
    if (teamOtherMode) return OTHER_CLUB;
    return teamSide;
  }, [teamOtherMode, teamSide]);

  const clubOptions = useMemo(() => {
    const seen = new Map();
    for (const q of fixtureQuotes) {
      const name = String(q?.player_team || '').trim();
      if (!name) continue;
      if (!seen.has(name.toLowerCase())) seen.set(name.toLowerCase(), name);
    }
    return [...seen.values()].sort((a, b) => a.localeCompare(b));
  }, [fixtureQuotes]);

  const { data: leagueOptions = [], isLoading: leaguesLoading } = useFilterLeagues();
  const { data: marketIdOptions = [], isLoading: marketIdsLoading } = useFilterMarketIds({
    enabled: scope === 'league_wager',
  });

  const createMut = useCreateBatchPublishSuspension();
  const removeMut = useRemoveBatchPublishSuspension();

  const showFixtureFields = isFixtureBatchScope(scope);
  const showLeague = scope === 'league' || scope === 'league_wager';
  const showWager = scope === 'league_wager';
  const showPlayer = scope === 'fixture_player';
  const showTeam = scope === 'fixture_team';

  const fixtureMissing = showFixtureFields && !String(fixtureId).trim();
  const playerMissing = showPlayer && !String(playerId).trim();
  // 'home' | 'away' for a game, or a club name / abbr for a futures shell. 64 is the DB column
  // width and the backend's own limit — reject here so the operator sees it before the POST.
  const teamValue = String(teamSide).trim();
  const teamIsSide = ['home', 'away'].includes(teamValue);
  const teamTooLong = !teamIsSide && teamValue.length > 64;
  const teamMissing = showTeam && (!teamValue || teamTooLong);
  const leagueMissing = showLeague && !String(league).trim();
  const wagerMissing = showWager && !String(wagerType).trim();
  const needsConfirmAll = scope === 'all' && !confirmAll;
  const busy = createMut.isPending || removeMut.isPending;

  useEffect(() => {
    if (!showFixtureFields || !selectedFixture) return;
    setSportBucket(sportBucketForFixture(selectedFixture));
  }, [showFixtureFields, selectedFixture]);

  // Clear the team selector when the fixture or scope changes: a club carried over from another
  // fixture stays valid-looking in the select but would match nothing (or the wrong markets).
  useEffect(() => {
    setTeamSide('');
    setTeamOtherMode(false);
  }, [fixtureId, scope]);

  const payload = useMemo(() => {
    const p = {
      sport_bucket: sportBucket,
      scope,
      reason: reason.trim() || 'Fixture suspension',
      created_by: createdBy.trim() || user?.email || 'dashboard',
      confirm_all_markets: scope === 'all' ? confirmAll : false,
      fanout_writers: fanoutWriters,
    };
    if (showFixtureFields) p.fixture_id = fixtureId.trim() || undefined;
    if (showPlayer) p.player_id = playerId.trim() || undefined;
    if (showTeam) p.team_side = teamSide.trim() || undefined;
    if (showLeague) p.league = league.trim() || undefined;
    if (showWager) p.wager_type = wagerType.trim() || undefined;
    return p;
  }, [
    sportBucket,
    scope,
    fixtureId,
    playerId,
    teamSide,
    league,
    wagerType,
    reason,
    createdBy,
    user?.email,
    confirmAll,
    fanoutWriters,
    showFixtureFields,
    showPlayer,
    showTeam,
    showLeague,
    showWager,
  ]);

  const runCreate = useCallback(() => {
    setLastError(null);
    setLastSuccess(null);
    createMut.mutate(payload, {
      onSuccess: (res) => setLastSuccess(res),
      onError: (e) => setLastError(formatApiError(e)),
    });
  }, [createMut, payload]);

  const runRemove = useCallback(
    async (id) => {
      const ok = await confirm({
        title: 'End suspension rule',
        body: `End rule #${id}? Writers pick it up on the next republish.`,
        confirmLabel: 'End rule',
      });
      if (!ok) return;
      setLastError(null);
      setLastSuccess(null);
      removeMut.mutate(id, {
        onSuccess: (res) => setLastSuccess(res),
        onError: (e) => setLastError(formatApiError(e)),
      });
    },
    [confirm, removeMut]
  );

  const scopeGroups = useMemo(() => {
    const groups = [
      { key: 'game', label: 'Game / team / player (same as Trading Dash)' },
      { key: 'league', label: 'League / bucket (disaster rules)' },
    ];
    return groups.map((g) => ({
      ...g,
      options: BATCH_SUSPENSION_SCOPE_OPTIONS.filter((o) => o.group === g.key),
    }));
  }, []);

  return (
    <div className="batch-susp-page">
      <header className="batch-susp-header">
        <h1>Batch suspension</h1>
        <p className="batch-susp-lede">
          Matching markets show as <strong>SUSPENDED</strong> in fixture payloads only. KF and{' '}
          <code>market_quotes</code> stay as-is. Use <strong>game / team / player</strong> scopes
          (same rules as Trading Dash) or league-level disaster rules.
        </p>
      </header>

      <div className="batch-susp-grid">
        <section className="batch-susp-card">
          <h2>New rule</h2>
          <label className="batch-susp-field">
            <span>Scope</span>
            <select
              value={scope}
              onChange={(e) => {
                const v = e.target.value;
                setScope(v);
                if (v === 'all') {
                  setLeague('');
                  setWagerType('');
                } else if (v === 'league') {
                  setWagerType('');
                }
                if (!isFixtureBatchScope(v)) {
                  setFixtureId('');
                  setPlayerId('');
                  setTeamSide('');
                }
                if (v !== 'fixture_player') setPlayerId('');
                if (v !== 'fixture_team') setTeamSide('');
              }}
              disabled={busy}
            >
              {scopeGroups.map((group) => (
                <optgroup key={group.key} label={group.label}>
                  {group.options.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </label>

          {showFixtureFields && (
            <label className="batch-susp-field">
              <span>Fixture</span>
              <select
                value={fixtureId}
                onChange={(e) => {
                  setFixtureId(e.target.value);
                  setPlayerId('');
                }}
                disabled={busy || fixturesLoading}
              >
                <option value="">
                  {fixturesLoading ? 'Loading fixtures…' : 'Select fixture'}
                </option>
                {fixtures.map((f) => (
                  <option key={f.game_id} value={f.game_id}>
                    {batchSuspensionFixtureLabel(f)} · {f.game_id}
                  </option>
                ))}
              </select>
              {fixtureId ? (
                <span className="batch-susp-muted batch-susp-field-hint">
                  Sport bucket auto-set to <code>{sportBucket}</code>
                </span>
              ) : null}
            </label>
          )}

          {showTeam && (
            <label className="batch-susp-field">
              <span>Team</span>
              <select
                value={teamSelectValue}
                onChange={(e) => {
                  const v = e.target.value;
                  // '__other__' keeps the row in club mode with an empty value so the text input
                  // below takes over; anything else is the selector itself.
                  setTeamSide(v === OTHER_CLUB ? '' : v);
                  setTeamOtherMode(v === OTHER_CLUB);
                }}
                disabled={busy || !fixtureId || (quotesLoading && !fixtureHasSides)}
              >
                <option value="">Select team</option>
                {fixtureHasSides && (
                  <>
                    <option value="home">
                      Home{selectedFixture?.home_team ? ` — ${selectedFixture.home_team}` : ''}
                    </option>
                    <option value="away">
                      Away{selectedFixture?.away_team ? ` — ${selectedFixture.away_team}` : ''}
                    </option>
                  </>
                )}
                {clubOptions.length > 0 && (
                  <optgroup label={fixtureHasSides ? 'Clubs (priced on this fixture)' : 'Clubs'}>
                    {clubOptions.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </optgroup>
                )}
                <option value={OTHER_CLUB}>Other club — type a name or abbr…</option>
              </select>
              {teamOtherMode && (
                <input
                  type="text"
                  value={teamSide}
                  onChange={(e) => setTeamSide(e.target.value)}
                  placeholder="e.g. Buffalo Bills or BUF"
                  maxLength={64}
                  disabled={busy || !fixtureId}
                />
              )}
              {!fixtureHasSides && (
                <span className="batch-susp-muted batch-susp-field-hint">
                  {quotesLoading
                    ? 'Loading clubs priced on this fixture…'
                    : `Futures shell — no home/away. Matched on the market's club${
                      clubOptions.length ? ` (${clubOptions.length} found)` : ', none discovered yet — type one'
                    }.`}
                </span>
              )}
              {teamTooLong && (
                <span className="batch-susp-muted batch-susp-field-hint">
                  Club selector must be at most 64 characters.
                </span>
              )}
            </label>
          )}

          {showPlayer && (
            <label className="batch-susp-field">
              <span>Player</span>
              <select
                value={playerId}
                onChange={(e) => setPlayerId(e.target.value)}
                disabled={busy || !fixtureId || quotesLoading}
              >
                <option value="">
                  {!fixtureId
                    ? 'Select a fixture first'
                    : quotesLoading
                      ? 'Loading players…'
                      : 'Select player'}
                </option>
                {playerOptions.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} ({p.id})
                  </option>
                ))}
              </select>
              {!quotesLoading && fixtureId && playerOptions.length === 0 ? (
                <span className="batch-susp-muted batch-susp-field-hint">
                  No quoted players for this fixture yet. Enter slug manually below.
                </span>
              ) : null}
              <input
                type="text"
                value={playerId}
                onChange={(e) => setPlayerId(e.target.value)}
                placeholder="Or type player slug (market_key segment)"
                disabled={busy || !fixtureId}
              />
            </label>
          )}

          {!showFixtureFields && (
            <label className="batch-susp-field">
              <span>Sport bucket</span>
              <select
                value={sportBucket}
                onChange={(e) => setSportBucket(e.target.value)}
                disabled={busy}
              >
                {BATCH_SUSPENSION_SPORT_BUCKETS.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </label>
          )}

          {showLeague && (
            <label className="batch-susp-field">
              <span>League</span>
              <select
                value={league}
                onChange={(e) => setLeague(e.target.value)}
                disabled={busy || leaguesLoading}
              >
                <option value="">
                  {leaguesLoading ? 'Loading leagues…' : 'Select league'}
                </option>
                {Array.isArray(leagueOptions) &&
                  leagueOptions.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
              </select>
            </label>
          )}

          {showWager && (
            <label className="batch-susp-field">
              <span>Market type</span>
              <select
                value={wagerType}
                onChange={(e) => setWagerType(e.target.value)}
                disabled={busy || marketIdsLoading}
              >
                <option value="">
                  {marketIdsLoading ? 'Loading…' : 'Select market type'}
                </option>
                {Array.isArray(marketIdOptions) &&
                  marketIdOptions.map((mid) => (
                    <option key={mid} value={mid}>
                      {mid}
                    </option>
                  ))}
              </select>
            </label>
          )}

          <label className="batch-susp-field">
            <span>Reason</span>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={busy}
            />
          </label>
          <label className="batch-susp-field">
            <span>Created by (optional)</span>
            <input
              type="text"
              value={createdBy}
              onChange={(e) => setCreatedBy(e.target.value)}
              placeholder={user?.email || 'defaults to your email'}
              disabled={busy}
            />
          </label>
          {scope === 'all' && (
            <label className="batch-susp-check batch-susp-check-warn">
              <input
                type="checkbox"
                checked={confirmAll}
                onChange={(e) => setConfirmAll(e.target.checked)}
                disabled={busy}
              />
              <span>I confirm: suspend <strong>all</strong> markets in this bucket</span>
            </label>
          )}
          <label className="batch-susp-check">
            <input
              type="checkbox"
              checked={fanoutWriters}
              onChange={(e) => setFanoutWriters(e.target.checked)}
              disabled={busy}
            />
            <span>
              {showFixtureFields && fixtureId
                ? 'Republish this fixture on writers now'
                : 'Republish writers in this bucket now'}
            </span>
          </label>
          <button
            type="button"
            className="batch-susp-btn batch-susp-btn-primary"
            disabled={
              busy ||
              fixtureMissing ||
              playerMissing ||
              teamMissing ||
              leagueMissing ||
              wagerMissing ||
              needsConfirmAll ||
              (showWager && marketIdsLoading)
            }
            onClick={runCreate}
          >
            {createMut.isPending ? 'Submitting…' : 'Create rule'}
          </button>
        </section>

        <section className="batch-susp-card">
          <div className="batch-susp-card-head">
            <h2>Active rules</h2>
            <button type="button" className="batch-susp-btn-ghost" onClick={() => refetch()} disabled={isLoading}>
              Refresh
            </button>
          </div>
          <label className="batch-susp-field batch-susp-field-inline">
            <span>Show</span>
            <select
              value={listSportFilter}
              onChange={(e) => setListSportFilter(e.target.value)}
              disabled={busy}
            >
              <option value="all">All sports</option>
              {BATCH_SUSPENSION_SPORT_BUCKETS.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label} only
                </option>
              ))}
            </select>
          </label>
          {isLoading && <p className="batch-susp-muted">Loading…</p>}
          {isError && (
            <p className="batch-susp-err">{formatApiError(error)}</p>
          )}
          {!isLoading && !isError && suspensions.length === 0 && (
            <p className="batch-susp-muted">No active rules.</p>
          )}
          {!isLoading && suspensions.length > 0 && (
            <ul className="batch-susp-list">
              {suspensions.map((row) => (
                <li key={row.id} className="batch-susp-row">
                  <div className="batch-susp-row-main">
                    <strong>#{row.id}</strong>{' '}
                    <code>{row.sport_bucket || '—'}</code>{' '}
                    <code>{batchSuspensionScopeLabel(row.scope)}</code>
                    <div className="batch-susp-target">{formatBatchSuspensionTarget(row)}</div>
                    <div className="batch-susp-reason">{row.reason}</div>
                    <div className="batch-susp-meta">
                      {row.created_by || '—'} · since {formatTs(row.valid_from)}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="batch-susp-btn batch-susp-btn-danger"
                    disabled={busy}
                    onClick={() => runRemove(row.id)}
                  >
                    End rule
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      {lastError && (
        <div className="batch-susp-banner batch-susp-banner-err">{lastError}</div>
      )}
      {lastSuccess && (
        <section className="batch-susp-card batch-susp-result">
          <h3>Last API response</h3>
          <pre className="batch-susp-pre">{JSON.stringify(lastSuccess, null, 2)}</pre>
        </section>
      )}
      <ModalLayer />
    </div>
  );
}
